import React, { PureComponent, Fragment, createRef } from 'react'
import PropTypes from 'prop-types'
import BEMHelper from '@ynap/bem-helper'
import { MultiStepForm, Step } from '@ynap/multi-step-form'
import { Button } from '@ynap/button'
import Overlay from '@ynap/overlay'
import Notification from '@ynap/notification'
import track from 'react-tracking'
import RemovedItemsOverlay from '../CartRectifyOverlay/RemovedItemsOverlay'
import ShippingRestrictionsOverlay from '../CartRectifyOverlay/ShippingRestrictionsOverlay'
import { redirectToCheckout, redirectToLogin } from '@ynap/redirect-utils'
import { RECTIFY_OUT_OF_STOCK, RECTIFY_SHIPPING_RESTRICTION, RESERVATION_NUMBER_INVALID } from '../../state/cart-state/actions/cartRectification'
import { LoginStepSkeleton } from '../LoginStep/LoginStep'
import Spinner from '@ynap/spinner'
import { withOptimizely, trackPage as trackPageOptimizely } from '@ynap/optimizely-utils'
import LiveChatButton from '../LiveChatButton/LiveChatButton'

const bem = new BEMHelper('PurchasePath')

@track()
class PurchasePath extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            showOverlay: false,
            popupError: null,
            headerElement: undefined,
        }
    }

    static propTypes = {
        lineItems: PropTypes.arrayOf(PropTypes.object),
        restrictedItems: PropTypes.arrayOf(PropTypes.object),
        location: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        addPromotion: PropTypes.func.isRequired,
        children: PropTypes.node.isRequired,
        locale: PropTypes.object.isRequired,
        messages: PropTypes.shape({
            purchasePath: PropTypes.shape({
                items: PropTypes.func,
                timeline: PropTypes.shape({
                    shipping: PropTypes.func,
                    reviewAndPay: PropTypes.func,
                    complete: PropTypes.func,
                }),
            }),
            orderReview: PropTypes.shape({
                DDPCountryInfo: PropTypes.func,
            }),
        }).isRequired,
        className: PropTypes.string,
        isFetchingCart: PropTypes.bool,
        isSkeleton: PropTypes.bool,
        isLoginSkeleton: PropTypes.bool,
        nativeapp: PropTypes.bool,
    }

    componentDidMount() {
        const { countries, fetchCountries, klarnaExpress } = this.props
        const { error: popupError } = klarnaExpress || {}

        if (popupError) {
            this.setState({ popupError })
        }

        if (!countries && fetchCountries) {
            fetchCountries()
        }

        const { fetchCart, lineItems, uberToken, locale, isSkeleton, optimizely, location } = this.props

        if (!isSkeleton) {
            if (!uberToken) {
                redirectToLogin(locale.id)
            } else if (!lineItems) {
                fetchCart({ requiresEmailAddress: true })
            } else if (!lineItems.length) {
                redirectToCheckout(locale.id)
            }
        }

        optimizely && trackPageOptimizely(optimizely, location.pathname, true)

        const headerElement = document.getElementById('header')

        if (headerElement) {
            this.setState({ headerElement })
        }
    }

    componentDidUpdate(prevProps) {
        const { lineItems, locale, optimizely, location } = this.props
        const prevPath = prevProps.location?.pathname

        if (!prevProps.lineItems && lineItems && !lineItems.length) {
            redirectToCheckout(locale.id)
        }

        if (optimizely && prevPath !== location?.pathname) {
            trackPageOptimizely(optimizely, location.pathname, true)
        }
    }

    closeErrorPopup() {
        const { setKlarnaExpress } = this.props
        this.setState({ popupError: null })
        setKlarnaExpress({ error: null })
    }

    renderRectification() {
        const {
            soldoutItems,
            lineItems,
            clearRectification,
            messages,
            rectification,
            history,
            setCheckoutNotification,
            locale,
            addItemsToWishlist,
            userStatus,
            restrictedItems,
            removeLineItems,
            lineItemsIncludingRemoved,
            checkoutError,
        } = this.props

        if (rectification) {
            if (rectification.rectificationType === RECTIFY_OUT_OF_STOCK || rectification.rectificationType === RESERVATION_NUMBER_INVALID) {
                return (
                    <RemovedItemsOverlay
                        soldoutItems={soldoutItems}
                        lineItems={lineItems}
                        lineItemsIncludingRemoved={lineItemsIncludingRemoved}
                        clearRectification={clearRectification}
                        messages={messages}
                        rectification={rectification}
                        history={history}
                        setCheckoutNotification={setCheckoutNotification}
                        locale={locale}
                        addItemsToWishlist={addItemsToWishlist}
                        userStatus={userStatus}
                        checkoutError={checkoutError}
                    />
                )
            }
            if (rectification.rectificationType === RECTIFY_SHIPPING_RESTRICTION) {
                return (
                    <ShippingRestrictionsOverlay
                        restrictedItems={restrictedItems}
                        clearRectification={clearRectification}
                        messages={messages}
                        rectification={rectification}
                        history={history}
                        setCheckoutNotification={setCheckoutNotification}
                        removeLineItems={removeLineItems}
                        locale={locale}
                        addItemsToWishlist={addItemsToWishlist}
                        userStatus={userStatus}
                    />
                )
            }
        }
        return null
    }

    render() {
        const {
            lineItems,
            messages,
            match,
            location,
            className,
            children,
            locale,
            isFetchingCart,
            isCheckingOut,
            isSkeleton,
            multipleLanguageCountries,
            isLoginSkeleton,
            config,
            nativeapp,
            ...rest
        } = this.props

        const {
            flags: { enableFastCheckoutAutoScroll = false },
            brand,
            liveChatAvailableLanguageList,
        } = config || {}

        const { country, language } = locale
        const isMultipleLanguageCountry = multipleLanguageCountries && Object.prototype.hasOwnProperty.call(multipleLanguageCountries, country)
        const isLiveChatEnabled = !isFetchingCart && !isSkeleton && !!lineItems?.length && !nativeapp && liveChatAvailableLanguageList?.includes(language)

        const { headerElement } = this.state

        if (isLoginSkeleton) {
            return <LoginStepSkeleton />
        }

        // shipping address -> no progress bar
        // shipping options -> 1/3 green
        // packaging options -> 2/3 green
        // so we can use that as our CSS modifier for the timeline fraction
        // location.pathname = /locale/checkout/<shipping-address|shipping-options|packaging-options>
        // split by '/' and get last element
        let modifier = location.pathname.split('/').pop()

        return (
            <div className={bem(null, className)}>
                {this.state.popupError && (
                    <Overlay
                        className={bem('popupErrorWrapper')}
                        title={messages.purchasePath.errorPopup?.()}
                        onClose={() => this.closeErrorPopup()}
                        handleBodyClick={() => this.closeErrorPopup()}
                    >
                        <div className={bem('popupErrorMessage')}>{messages.errors.klarnaExpress?.()}</div>
                        <Button className={bem('popupErrorButton')} onClick={() => this.closeErrorPopup()} type="primary">
                            {messages.basket.proceedToPurchase?.()}
                        </Button>
                    </Overlay>
                )}

                {!isSkeleton && this.renderRectification()}

                {isMultipleLanguageCountry && country !== 'DE' && (
                    <Notification className={bem('languagePrompt')} message={messages.purchasePath.languageCheckoutPrompt()} type="info" />
                )}
                <div className={bem('timeline')}>
                    <MultiStepForm location={location} match={match} horizontal={true} timeline={true} reverse={location.pathname.includes('confirmation')}>
                        {/* path regex to account for shipping-address/shipping-options/packaging-options */}
                        <Step
                            editable={false}
                            path="/:locale/checkout/(shipping-address|shipping-options|packaging-options)/:anchor?"
                            messages={messages}
                            title={messages.purchasePath.timeline.shipping()}
                            completed={true}
                            exact={true}
                            small={true}
                            timeline={true}
                            locale={locale}
                            className={bem('step')}
                            {...rest}
                        />
                        <Step
                            editable={false}
                            path="/:locale/checkout/payment-options"
                            messages={messages}
                            title={messages.purchasePath.timeline.reviewAndPay()}
                            id={'2'}
                            completed={true}
                            exact={true}
                            timeline={true}
                            small={true}
                            locale={locale}
                            className={bem('step', modifier)}
                            {...rest}
                        />
                        <Step
                            editable={false}
                            path="/:locale/checkout/order-confirmation"
                            completedOnMatch={true}
                            messages={messages}
                            title={messages.purchasePath.timeline.complete()}
                            id={'4'}
                            completed={true}
                            exact={true}
                            small={true}
                            timeline={true}
                            locale={locale}
                            className={bem('step')}
                            {...rest}
                        />
                    </MultiStepForm>
                </div>
                <MultiStepForm
                    className={bem('steps')}
                    location={location}
                    match={match}
                    isFetching={!!isFetchingCart}
                    fastCheckoutAutoScroll={enableFastCheckoutAutoScroll}
                    headerElement={headerElement}
                >
                    {children}
                </MultiStepForm>
                {isLiveChatEnabled ? <LiveChatButton brand={brand} language={language} messages={messages} /> : null}
                {isCheckingOut ? (
                    <Overlay className={bem('payment', null, 'overlay')}>
                        <div id="threeDS2" className={bem('threeDS2Wrapper')} />
                        <div className={bem('payment-loading')}>
                            <Spinner className={bem('spinner')} />
                            <span>{messages.purchasePath.paymentProcessing()}</span>
                        </div>
                    </Overlay>
                ) : null}
            </div>
        )
    }
}

export default withOptimizely(PurchasePath)
